import { Controller } from "@hotwired/stimulus"

/**
 * Toggle between a chart canvas and a data table.
 *
 * Attached to a button with:
 *   data-controller="chart-table-toggle"
 *   data-chart-table-toggle-target-value="<canvas_id>"
 *   data-chart-table-toggle-table-id-value="<table_id>"   (optional)
 *   data-action="click->chart-table-toggle#toggle"
 *
 * If no explicit `table-id-value` is provided, we look for a sibling
 * element with id "<canvas_id>-table" (a conventional default). If no
 * table exists — common in Phase 1 where the data-table rendering is
 * deferred — toggle is a no-op so the button remains inert without error.
 *
 * Accessibility:
 *   - Updates aria-pressed / aria-expanded on the button.
 *   - Uses textContent (not innerHTML) per the plan's a11y note.
 */
export default class extends Controller {
  static values = {
    target:     { type: String, default: "" },        // canvas id
    tableId:    { type: String, default: "" },        // table id (optional)
    chartLabel: { type: String, default: "View as Table" },
    tableLabel: { type: String, default: "View as Chart" }
  }

  connect() {
    this.showingTable = false
    this.element.setAttribute("aria-pressed", "false")
    this.element.setAttribute("aria-expanded", "false")
    // Only overwrite label text when the button has no existing icon-only
    // content. Views in Task 8 use an <i class="bi bi-table"> icon child
    // and keep the label in aria-label/title — don't clobber that.
    if (this._isTextOnlyButton()) {
      this.element.textContent = this.chartLabelValue
    }
  }

  toggle(event) {
    if (event) event.preventDefault()

    // The id-targeted node is either a Chartkick wrapper <div> (for
    // area/column/bar charts) or the matrix heatmap's bare <canvas>. We
    // toggle whichever one was given the id so layout stays stable —
    // hiding the wrapper collapses the chart area cleanly, hiding the
    // canvas alone would leave an empty Chartkick div.
    const chartNode = document.getElementById(this.targetValue)
    const table = document.getElementById(this._resolvedTableId())

    if (!chartNode || !table) {
      // Silent no-op when the table sibling hasn't been rendered yet.
      return
    }

    this.showingTable = !this.showingTable
    chartNode.classList.toggle("d-none", this.showingTable)
    table.classList.toggle("d-none", !this.showingTable)

    // Hide any "bar-only" helper text (e.g. "Click a bar to drill in…") while
    // the table is showing — there are no bars in the table view. Scoped to
    // THIS card via the toggle button's closest .report-chart-card ancestor,
    // so unrelated cards on the page are never touched. No-op when the card
    // has no such helpers.
    this._barOnlyHelpers().forEach((el) => {
      el.classList.toggle("d-none", this.showingTable)
    })

    const pressed = this.showingTable ? "true" : "false"
    this.element.setAttribute("aria-pressed", pressed)
    this.element.setAttribute("aria-expanded", pressed)

    if (this._isTextOnlyButton()) {
      this.element.textContent = this.showingTable ? this.tableLabelValue : this.chartLabelValue
    }
  }

  _resolvedTableId() {
    if (this.tableIdValue) return this.tableIdValue
    return this.targetValue ? `${this.targetValue}-table` : ""
  }

  // Bar-only helper element(s) scoped to this toggle button's chart card.
  // The controller is attached to the toggle BUTTON in the card header, so we
  // walk up to the enclosing .report-chart-card and query within it. Returns
  // an array (possibly empty) so callers can iterate safely.
  _barOnlyHelpers() {
    const card = this.element.closest(".report-chart-card")
    if (!card) return []
    return Array.from(card.querySelectorAll("[data-chart-table-toggle-bar-only]"))
  }

  _isTextOnlyButton() {
    // If the button contains any element children (icons), leave the DOM
    // alone and rely on aria attributes + title for screen-reader context.
    return this.element.children.length === 0
  }
}
